import React, { useCallback, useEffect, useState } from "react";
import FileSystemCard from "./components/FileSystemCard";
import SearchAndNew from "components/file-system/SearchAndNewFileOrFolder";
import Loader from "components/Loader";
import { FileSystemItem } from "types";
import api from "api/axios";
import { handleAxiosAndCaughtError } from "utils";
import { useLibraryContext } from "../context/LibraryContext";
import { useTranslation } from "react-i18next";

const MainPage = () => {
  const { t } = useTranslation();
  const { query, setQuery } = useLibraryContext();
  const [files, setFiles] = useState<FileSystemItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const translateFolderName = useCallback(
    (name: string): string => {
      const lowerName = name.toLowerCase();
      // Map folder names to translation keys
      const folderTranslationMap: Record<string, string> = {
        documents: "routes.documents",
        workflows: "routes.workflows",
      };

      const translationKey = folderTranslationMap[lowerName];
      return translationKey ? t(translationKey) : name;
    },
    [t]
  );

  const fetchFiles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await api.get("/folders");
      setFiles(
        resp.data.map((item: FileSystemItem) => ({
          ...item,
          type: item.type || "Folder",
          typeName: item.name,
          name: translateFolderName(item.name),
        }))
      );
    } catch (err) {
      setError(t("library.errorFetchingFiles"));
      handleAxiosAndCaughtError(err);
    } finally {
      setLoading(false);
    }
  }, [t, translateFolderName]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  if (loading) {
    return (
      <Loader
        txt={t("library.gettingItems")}
        className="absolute left-1/2 top-1/2  -translate-y-1/2 translate-x-1/2"
      />
    );
  }

  if (error || files.length === 0) {
    return (
      <div className="absolute left-1/2 top-1/2 flex  -translate-y-1/2  translate-x-1/2 flex-col  items-center justify-center gap-2  ">
        <p className="text-lg font-medium text-red-500">{error}</p>
        <button
          onClick={fetchFiles}
          className="rounded-lg bg-blue-500 px-4 py-1 text-white hover:bg-blue-600"
        >
          {t("common.retry")}
        </button>
      </div>
    );
  }
  const filteredItems =
    files?.filter((item) =>
      item.name.toLowerCase().includes(query.toLowerCase())
    ) || [];

  return (
    <div className=" h-full w-full rounded-[20px]">
      <div className="mt-[70px] w-full">
        <SearchAndNew value={query} onChange={setQuery} showAdd={false} />
      </div>
      <div className="ml-[23px]">
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {filteredItems?.map((folder) => (
            <FileSystemCard key={folder.id} item={folder} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default React.memo(MainPage);
